import { useAuth0 } from '@auth0/auth0-react';
import { IconNames } from '@blueprintjs/icons';
import { datadogRum } from '@datadog/browser-rum';
import { openInNewTab } from '@strata/helper-functions';
import { TermsOfUseDialog } from '@strata/react-dialog';
import { Header as StrataHeader } from '@strata/react-header';
import { BaseNotification } from '@strata/react-notifications';
import { Badge } from '@strata/react-status-icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ComponentProps, useEffect, useState } from 'react';

import { getHasAcceptedTermsOfUse, postAcceptTermsOfUse } from 'api/authAPI';
import { UserNotificationSettingsDialog } from 'components/header/dialogs/notificationSettings/UserNotificationSettingsDialog';
import { UserProfileDialog } from 'components/header/dialogs/UserProfileDialog';
import { FeatureCarousel } from 'components/header/FeatureCarousel';
import { NotificationComponentMap } from 'components/notifications/NotificationComponentMap';
import { handleError } from 'services/Error';
import { SupplierFeatures, useSupplierFeatures } from 'services/FeatureFlags';
import { AppRoutes } from 'services/routes';
import { useAppDispatch, useAppSelector } from 'store';
import { useGetHomeScreenMessagesQuery } from 'store/api/announcementApi';
import {
    clearIncomingNotification,
    updateNotificationStatus,
    updateNotificationStatuses,
} from 'store/notification/notificationThunks';

export const Header = () => {
    const { isAuthenticated, logout } = useAuth0();
    const { showAppVersion } = useFlags<{ showAppVersion: boolean }>();

    const supplierFeatures = useSupplierFeatures();
    const dispatch = useAppDispatch();

    const { toDoTaskCount } = useAppSelector((state) => state.worklist);
    const { unreadConversationCount } = useAppSelector((state) => state.userSupport);

    const navTabs = isAuthenticated
        ? [
              {
                  label: 'Worklist',
                  to: AppRoutes.worklist,
                  badgeCount: toDoTaskCount,
              },
              { label: 'Live Referrals', to: AppRoutes.liveReferrals },
              {
                  label: 'Manage Referrals',
                  to: AppRoutes.manageReferrals,
                  children: [
                      { label: 'My Referrals', to: AppRoutes.wonReferrals },
                      { label: 'Upcoming Referrals', to: AppRoutes.upcomingReferrals },
                      StrataHeader.DIVIDER,
                      { label: 'Expired Referrals', to: AppRoutes.expiredReferrals },
                      { label: 'Declined Referrals', to: AppRoutes.declinedReferrals },
                      { label: 'Lost Referrals', to: AppRoutes.lostReferrals },
                      ...(supplierFeatures?.[SupplierFeatures.MEDBILLING]
                          ? [
                                StrataHeader.DIVIDER,
                                { label: 'Claims History', to: AppRoutes.claimsHistory },
                            ]
                          : []),
                  ],
              },
              {
                  label: 'All Messages',
                  to: AppRoutes.allMessages,
                  badgeIntent: Badge.Intent.UNREAD,
                  badgeCount: unreadConversationCount,
              },
              {
                  label: 'Admin',
                  to: AppRoutes.admin,
                  children: [
                      { label: 'Automations', to: AppRoutes.automations },
                      { label: 'Prescriber Lists', to: AppRoutes.prescriberLists },
                      { label: 'State Lists', to: AppRoutes.stateLists },
                      StrataHeader.DIVIDER,
                      {
                          icon: IconNames.SHARE,
                          label: 'User Access Request',
                          to: 'https://freemarkethealth.typeform.com/to/OpilSczA',
                          openInNewTab: true,
                      },
                      {
                          icon: IconNames.SHARE,
                          label: 'Pharmacy Change Request',
                          to: 'https://freemarkethealth.typeform.com/to/qYBQtgb2',
                          openInNewTab: true,
                      },
                  ],
              },
          ]
        : [];

    const [showUserNotificationSettingsDialog, setShowNotificationSettingsDialog] = useState(false);
    const [showUserProfileDialog, setShowUserProfileDialog] = useState(false);

    // Load home screen messages & carousels on login
    useGetHomeScreenMessagesQuery(undefined, { skip: !isAuthenticated });

    // Check if user has accepted terms of use on login
    const [showTermsDialog, setShowTermsDialog] = useState(false);
    useEffect(() => {
        (async () => {
            try {
                if (isAuthenticated) {
                    const result = await getHasAcceptedTermsOfUse();
                    setShowTermsDialog(!result?.accepted);
                }
            } catch (err) {
                handleError('Error retrieving terms acceptance status.', err as Error);
            }
        })();
    }, [isAuthenticated]);

    const onAcceptTermsClick = () => {
        (async () => {
            try {
                setShowTermsDialog(false);
                await postAcceptTermsOfUse();
            } catch (err) {
                handleError('Error accepting terms.', err as Error);
            }
        })();
    };

    const { notifications, incomingNotification } = useAppSelector((state) => state.notification);

    // Prevent incoming notifications from re-appearing
    useEffect(() => {
        dispatch(clearIncomingNotification());
    }, [incomingNotification, dispatch]);

    const onNotificationsDropdownClose = () => {
        const newlySeenIds = notifications
            .filter((notification) => notification.status === BaseNotification.Statuses.NEW)
            .map((notification) => notification.id);
        if (newlySeenIds.length > 0) {
            dispatch(updateNotificationStatuses(BaseNotification.Statuses.SEEN, newlySeenIds));
        }
    };
    const onNotificationClick: ComponentProps<typeof StrataHeader>['onNotificationClick'] = (
        id,
    ) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.INTERACTED, id));
    };
    const onRemoveNotificationClick: ComponentProps<
        typeof StrataHeader
    >['onRemoveNotificationClick'] = (id) => {
        dispatch(updateNotificationStatus(BaseNotification.Statuses.DELETED, id));
    };
    const onClearAllNotificationsClick = () => {
        const idList = notifications.map((notification) => notification.id);
        dispatch(updateNotificationStatuses(BaseNotification.Statuses.DELETED, idList));
    };

    const onLogOutClick = () => {
        (async () => {
            sessionStorage.clear();
            datadogRum.clearUser();
            await logout({
                logoutParams: { returnTo: `${window.location.origin}${AppRoutes.loggedOut}` },
            });
        })();
    };

    return (
        <>
            <StrataHeader
                navBar={navTabs}
                onProfileClick={() => setShowUserProfileDialog(true)}
                onNotificationSettingsClick={() => setShowNotificationSettingsDialog(true)}
                onLogOutClick={onLogOutClick}
                showNotifications={isAuthenticated}
                notifications={notifications}
                incomingNotification={incomingNotification}
                NotificationComponentMap={NotificationComponentMap}
                onNotificationClick={onNotificationClick}
                onRemoveNotificationClick={onRemoveNotificationClick}
                onClearAllNotificationsClick={onClearAllNotificationsClick}
                onNotificationsDropdownClose={onNotificationsDropdownClose}
                showKnowledgeCenterDropdown={isAuthenticated}
                showProfileDropdown={isAuthenticated}
                onTermsOfUseClick={() => openInNewTab(AppRoutes.termsOfUse)}
                onPrivacyPolicyClick={() => openInNewTab(AppRoutes.privacyPolicy)}
                appVersion={showAppVersion ? process.env.REACT_APP_VERSION : undefined}
            />
            {isAuthenticated && (
                <>
                    <TermsOfUseDialog
                        isOpen={showTermsDialog}
                        onAcceptClick={onAcceptTermsClick}
                        onSignOutClick={onLogOutClick}
                    />
                    {!showTermsDialog && <FeatureCarousel />}
                    <UserNotificationSettingsDialog
                        onSecondaryButtonClick={() => setShowNotificationSettingsDialog(false)}
                        isOpen={showUserNotificationSettingsDialog}
                    />
                    <UserProfileDialog
                        onSecondaryButtonClick={() => setShowUserProfileDialog(false)}
                        isOpen={showUserProfileDialog}
                    />
                </>
            )}
        </>
    );
};
